import React, { useEffect, useState } from 'react'
import { getAssetUrl } from '../../../helpers/backend_helper'
import {
  Button,
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Form,
  FormGroup,
  FormFeedback
} from 'reactstrap'
import { IEffect, IPhotoBooth } from './index'
import { toast } from 'react-toastify'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import Select from 'react-select'
import { customStylesLabel } from '../../../lib/util'

interface IProps {
  effectRecord: IEffect | undefined
  isOpen: boolean
  editMode: boolean
  setOpenPopup: (arg: boolean) => void
  handleCreateOrUpdateEffect: (arg: any) => Promise<string>
  photoBooths: IPhotoBooth[]
}

const initEffect: IEffect = {
  name: '',
  imageLink: '',
  translations: {},
  isActive: true,
  isActiveFor: []
}

const EffectModal: React.FC<IProps> = ({
  effectRecord,
  isOpen,
  editMode,
  handleCreateOrUpdateEffect,
  setOpenPopup,
  photoBooths
}) => {
  const [effectState, setEffectState] = useState<IEffect>(initEffect)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const hasEdit = (editMode && effectRecord) || false

  const boothOptions = photoBooths.map((booth) => ({
    value: booth._id,
    label: booth.name
  }))

  const formik: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: hasEdit ? effectRecord?.name : '',
      imageLink: hasEdit ? effectRecord?.imageLink : ''
    },
    validationSchema: Yup.object({
      name: Yup.string().required('İsim zorunludur'),
      imageLink: Yup.string()
    }),
    onSubmit: () => {
      handleSubmit()
    }
  })

  useEffect(() => {
    if (editMode && effectRecord) {
      setEffectState(effectRecord)
    } else {
      setEffectState(initEffect)
    }

    if (isOpen) {
      formik.resetForm()
      setSelectedFile(null)
    }
  }, [isOpen, editMode, effectRecord])

  const togglePopup = () => {
    setOpenPopup(!isOpen)
  }

  const handleOnChange = (e: { target: { name: string; value: string } }) => {
    const { name, value } = e.target
    setEffectState({
      ...effectState,
      [name]: value
    })
    formik.setFieldValue(name, value)
  }

  const handleBoothsChange = (selectedOptions: any) => {
    const selectedIds = selectedOptions ? selectedOptions.map((opt: any) => opt.value) : []
    setEffectState({
      ...effectState,
      isActiveFor: selectedIds
    })
  }

  const handleActiveChange = (e: { target: { checked: boolean } }) => {
    setEffectState({
      ...effectState,
      isActive: e.target.checked
    })
  }

  const handleSubmit = async () => {
    const formData = new FormData()
    if (selectedFile) {
      formData.append('image', selectedFile)
    } else if (editMode && effectState.imageLink) {
      formData.append('imageLink', effectState.imageLink)
    }
    formData.append('name', effectState.name || '')
    formData.append('isActiveFor', JSON.stringify(effectState.isActiveFor || []))
    formData.append('isActive', String(effectState.isActive))
    formData.append('translations', JSON.stringify(effectState.translations || {}))
    if (editMode && effectRecord?._id) {
      formData.append('_id', effectRecord._id)
    }

    const message = await handleCreateOrUpdateEffect(formData)
    if (message === 'SUCCESSFULL') {
      toast.success(
        editMode ? 'Filtre başarıyla güncellendi' : 'Yeni filtre başarıyla oluşturuldu'
      )
      setOpenPopup(false)
    } else {
      toast.error(message)
    }
  }

  const selectedBooths = boothOptions.filter((opt) =>
    effectState.isActiveFor?.includes(opt.value)
  )

  return (
    <React.Fragment>
      <Modal
        isOpen={isOpen}
        toggle={togglePopup}
        scrollable={true}
        id='effectModal'
        size='lg'
      >
        <ModalHeader className='modal-title' id='effectModalTitle' toggle={togglePopup}>
          {editMode ? 'Filtre Düzenle' : 'Yeni Filtre Oluştur'}
        </ModalHeader>
        <ModalBody>
          <Form
            className='needs-validation'
            onSubmit={(e) => {
              e.preventDefault()
              formik.handleSubmit()
              return false
            }}
          >
            <Row>
              <FormGroup>
                <Col md={12} className='mb-3'>
                  <Label htmlFor='name' className='form-label mb-1 fw-semibold'>
                    İsim
                  </Label>
                  <Input
                    type='text'
                    className='form-control'
                    id='name'
                    name='name'
                    placeholder='Filtre ismi'
                    value={effectState?.name}
                    onChange={handleOnChange}
                    invalid={formik.touched.name && formik.errors.name ? true : false}
                  />
                  {formik.touched.name && formik.errors.name ? (
                    <FormFeedback type='invalid'>{formik.errors.name}</FormFeedback>
                  ) : null}
                </Col>
              </FormGroup>

              <FormGroup>
                <Col md={12} className='mb-3'>
                  <Label htmlFor='imageFile' className='form-label mb-1 fw-semibold'>
                    Görsel Yükle
                  </Label>
                  <Input
                    type='file'
                    className='form-control'
                    id='imageFile'
                    name='imageFile'
                    accept='image/*'
                    onChange={(e) => {
                      const file = e.target.files?.[0] || null
                      setSelectedFile(file)
                      if (file) {
                        formik.setFieldValue('imageLink', file.name)
                      }
                    }}
                  />
                  {selectedFile && (
                    <div className='mt-2'>
                      <img
                        src={URL.createObjectURL(selectedFile)}
                        alt='Preview'
                        style={{ maxWidth: '200px', maxHeight: '150px', objectFit: 'cover', borderRadius: '8px' }}
                      />
                    </div>
                  )}
                  {!selectedFile && editMode && effectState.imageLink && (
                    <div className='mt-2'>
                      <img
                        src={getAssetUrl(effectState.imageLink)}
                        alt='Current'
                        style={{ maxWidth: '200px', maxHeight: '150px', objectFit: 'cover', borderRadius: '8px' }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none'
                        }}
                      />
                      <small className='d-block text-muted mt-1'>Mevcut görsel</small>
                    </div>
                  )}
                </Col>
              </FormGroup>

              <FormGroup>
                <Col md={12} className='mb-3'>
                  <Label className='form-label mb-1 fw-semibold'>
                    Aktif Olduğu Booth'lar
                  </Label>
                  <div className='d-flex align-items-center gap-2 mb-2'>
                    <Button
                      color='soft-primary'
                      size='sm'
                      type='button'
                      onClick={() => {
                        setEffectState({ ...effectState, isActiveFor: boothOptions.map(b => b.value) })
                      }}
                    >
                      Select All
                    </Button>
                    <Button
                      color='soft-secondary'
                      size='sm'
                      type='button'
                      onClick={() => {
                        setEffectState({ ...effectState, isActiveFor: [] })
                      }}
                    >
                      Clear All
                    </Button>
                    <span className='text-muted fs-12'>
                      {selectedBooths.length} / {boothOptions.length} selected
                    </span>
                  </div>
                  <Select
                    isMulti
                    options={boothOptions}
                    value={selectedBooths}
                    onChange={handleBoothsChange}
                    placeholder='Booth seçiniz...'
                    className='basic-multi-select'
                    classNamePrefix='select'
                    menuPortalTarget={document.body}
                    menuPosition='fixed'
                    styles={customStylesLabel}
                  />
                  <small className='text-muted'>
                    Bu filtrenin hangi booth'larda görüneceğini seçin
                  </small>
                </Col>
              </FormGroup>

              <FormGroup>
                <Col md={12} className='mb-3'>
                  <div className='form-check form-switch'>
                    <Input
                      type='checkbox'
                      className='form-check-input'
                      id='isActive'
                      checked={effectState?.isActive}
                      onChange={handleActiveChange}
                    />
                    <Label className='form-check-label' htmlFor='isActive'>
                      Aktif
                    </Label>
                  </div>
                </Col>
              </FormGroup>
            </Row>

            <div className='modal-footer'>
              <Button color='light' onClick={() => setOpenPopup(false)}>
                Kapat
              </Button>
              <Button color='primary' type='submit'>
                Kaydet
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>
    </React.Fragment>
  )
}

export default EffectModal
